import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getCurrentUser, updateCurrentUser } from '../services/userService';
import './ProfilePage.css';

const beltRankOptions = [
  { value: '', label: '-- Select Belt Rank --' },
  { value: 'White', label: 'White Belt' },
  { value: 'Orange', label: 'Orange Belt' },
  { value: 'Purple', label: 'Purple Belt' },
  { value: 'Blue', label: 'Blue Belt' },
  { value: 'Green', label: 'Green Belt' },
  { value: 'Brown', label: 'Brown Belt' },
  { value: 'Black', label: 'Black Belt' },
  { value: 'BlackSecondDegree', label: 'Second Degree Black Belt' },
  { value: 'BlackThirdDegree', label: 'Third Degree Black Belt' },
  { value: 'BlackFourthDegree', label: 'Fourth Degree Black Belt' },
  { value: 'BlackFifthDegree', label: 'Fifth Degree Black Belt' },
  { value: 'BlackSixthDegree', label: 'Sixth Degree Black Belt' },
  { value: 'BlackSeventhDegree', label: 'Seventh Degree Black Belt' },
  { value: 'BlackEigthDegree', label: 'Eighth Degree Black Belt' },
  { value: 'BlackNinthDegree', label: 'Ninth Degree Black Belt' },
  { value: 'BlackTenthDegree', label: 'Tenth Degree Black Belt' },
];

export default function ProfilePage({ currentUser, onUserUpdated }) {
  const { accessToken, logout } = useAuth();
  const [email, setEmail] = useState(currentUser?.email || '');
  const [name, setName] = useState(currentUser?.name || '');
  const [beltRank, setBeltRank] = useState(currentUser?.beltRank || '');
  const [loading, setLoading] = useState(!currentUser);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (currentUser) {
      setEmail(currentUser.email || '');
      setName(currentUser.name || '');
      setBeltRank(currentUser.beltRank || '');
      setLoading(false);
      return;
    }

    if (!accessToken) {
      logout({ shouldCallApi: false });
      return;
    }

    const loadUser = async () => {
      try {
        const user = await getCurrentUser(accessToken);
        setEmail(user?.email || '');
        setName(user?.name || '');
        setBeltRank(user?.beltRank || '');
      } catch (err) {
        if (err?.status === 401) {
          await logout({ shouldCallApi: false });
          return;
        }
        setError(err.message || 'Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [accessToken, currentUser, logout]);

  const handleSave = async (event) => {
    event.preventDefault();

    if (!email || !name) {
      setError('Email and name are required.');
      setSuccessMessage(null);
      return;
    }

    if (!accessToken) {
      await logout({ shouldCallApi: false });
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updatedUser = await updateCurrentUser(
        {
          email,
          name,
          beltRank: beltRank || null,
        },
        accessToken
      );

      setEmail(updatedUser?.email || '');
      setName(updatedUser?.name || '');
      setBeltRank(updatedUser?.beltRank || '');
      setSuccessMessage('Profile updated.');
      onUserUpdated?.(updatedUser);
    } catch (err) {
      if (err?.status === 401) {
        await logout({ shouldCallApi: false });
        return;
      }
      setError(err.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="profile-page">
      <Paper className="profile-card" elevation={1}>
        <Typography variant="h5" component="h1" gutterBottom>
          Profile
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Keep your account details up to date.
        </Typography>

        {loading ? (
          <Typography variant="body2">Loading profile...</Typography>
        ) : (
          <Box component="form" onSubmit={handleSave}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {successMessage && <Alert severity="success">{successMessage}</Alert>}

              <TextField
                label="Email (read-only)"
                type="email"
                value={email}
                autoComplete="email"
                InputProps={{ readOnly: true }}
                required
                fullWidth
              />

              <TextField
                label="Name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="profile-belt-rank-label">Belt Rank</InputLabel>
                <Select
                  labelId="profile-belt-rank-label"
                  value={beltRank}
                  label="Belt Rank"
                  onChange={(e) => setBeltRank(e.target.value)}
                >
                  {beltRankOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
